import React, { useMemo } from 'react';
import { TrendingUp, Building, ShieldCheck, PiggyBank, Gem } from 'lucide-react';
import { CurrencyInput, AllocationInput } from '../ui/index.tsx';
import { buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { calculateAge } from '../../utils/index.ts';
import SupportAtHomeAnnuityConfigurator, {
    type AnnuityDetails,
    type AnnuityFundingSource,
} from '../../features/supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { INVESTMENT_PROFILES, newMoneyProfileOf, investmentBondNetPctFor, INVESTMENT_BOND_TAX_FREE_YEARS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { nonConcessionalCapacityForHousehold, nonConcessionalBlockerWords } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import { canCommenceAbpFromDownsizer } from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { NewMoneyProfileField } from './NewMoneyProfileField.tsx';
import { isIncomeOnlyAnnuity, incomeOnlySetter } from './IncomeOnlyTick.tsx';
import { AnnuityCeilingCaution } from './AnnuityCeilingCaution.tsx';

const formatCurrency = (value: number | string | null | undefined): string => {
    if (value == null || value === '' || isNaN(Number(value))) return '$0';
    const n = Math.round(Number(value));
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-AU')}`;
};

const num = (v: number | string | null | undefined): number => Math.max(0, parseFloat(String(v)) || 0);

/** Deployment config for a residual lump sum (see shared/src/calculation/residualDeployment.ts's
 *  ResidualDeployment) — mutable/draft-editable, so numeric fields accept a live-editing string. */
/**
 * Who owns a deployed annuity when nobody has said.
 *
 * JOINT for a couple, which is the kind default: a jointly owned annuity keeps paying the survivor
 * automatically, where a one-name non-reversionary annuity stops on the owner's death. Named here
 * rather than inlined because `deploymentAnnuityTerms` below has to report the SAME default the
 * configurator applies — two copies of it would let the screen disclose one owner while the
 * projection modelled another.
 */
export function deploymentAnnuityOwner(
    value: { annuityOwner?: string },
    isCouple: boolean,
): 'client' | 'partner' | 'joint' {
    return (value.annuityOwner as 'client' | 'partner' | 'joint') || (isCouple ? 'joint' : 'client');
}

/**
 * The annuity's terms as this deployment will actually build it — for disclosure on a card.
 *
 * Owner-raised, 2026-09-01: the Downsizer's engine cards named the residual in the strategy title
 * and said nothing about who owned the annuity or whether the income survived them. Derived from the
 * same `value` the deployment is built from, so there is nothing for the card to get wrong.
 */
export function deploymentAnnuityTerms(
    value: ProceedsDeploymentValue | null | undefined,
    isCouple: boolean,
): Record<string, unknown> | null {
    const purchaseAmount = Math.max(0, parseFloat(String(value?.annuity ?? 0)) || 0);
    if (!(purchaseAmount > 0)) return null;
    const owner = deploymentAnnuityOwner(value || {}, isCouple);
    return {
        purchaseAmount,
        residualPercent: Math.min(100, Math.max(0, parseFloat(String(value?.annuityResidualPercent ?? 0)) || 0)),
        owner,
        /*
         * A JOINT annuity continues to the survivor by its ownership — there is no separate
         * reversionary nomination to make, and reporting "not reversionary" for one would be true
         * to the field and false to the client. What they want to know is whether the income
         * survives them, and for a joint annuity it does.
         */
        reversionary: owner === 'joint' || !!value?.annuityReversionary,
        indexed: !!value?.annuityIndexation,
        annuityType: value?.annuityType === 'fixedTerm' ? 'fixedTerm' : value?.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
        termYears: value?.annuityType === 'fixedTerm' ? (Math.max(0, parseFloat(String(value?.annuityTermYears ?? 0)) || 0) || null) : null,
        deferralYears: value?.annuityType === 'fixedTerm' ? 0 : Math.max(0, Math.round(parseFloat(String(value?.annuityDeferralYears ?? 0)) || 0)),
    };
}

/**
 * Whether the deployment's annuity is "income only" — lifetime, nothing returned — which a hand-built
 * plan may not be saved with until the client has ticked that nothing comes back (owner, 21 Oct 2026).
 */
export function deploymentIsIncomeOnly(value: ProceedsDeploymentValue | null | undefined): boolean {
    if (!value) return false;
    return isIncomeOnlyAnnuity({
        purchaseAmount: value.annuity,
        annuityType: value.annuityType,
        residualCapital: (parseFloat(String(value.annuityResidualPercent ?? 0)) || 0) / 100,
    });
}

/**
 * A NEW GUARANTEED INCOME STARTS AS "MONEY BACK" (owner, 21 Oct 2026), as the village's does: a lifetime
 * annuity with the purchase price returned. Only when money first goes into it — an annuity already
 * holding money keeps whatever shape was chosen or saved. Income only is a choice, with a tick.
 */
export function deploymentStartingShape(value: ProceedsDeploymentValue | null | undefined, field: string, next: number | undefined): Partial<ProceedsDeploymentValue> {
    const had = parseFloat(String(value?.annuity ?? 0)) || 0;
    return field === 'annuity' && !(had > 0) && (Number(next) || 0) > 0 ? { annuityType: 'lifetime', annuityResidualPercent: 100 } : {};
}

export interface ProceedsDeploymentValue {
    shares?: number | string;
    investmentProperty?: number | string;
    ipRent?: number | string;
    ipExpenses?: number | string;
    annuity?: number | string;
    annuityType?: 'lifetime' | 'fixedTerm' | 'guaranteed';
    annuityOwner?: string;
    annuityResidualPercent?: number | string;
    annuityTermYears?: number | string;
    /** Lifetime only: years before the payments start. */
    annuityDeferralYears?: number | string;
    annuityIndexation?: boolean;
    annuityReversionary?: boolean;
    annuityIncomeOverride?: number | string | null;
    /** After-tax super contributions, per person (capped by the rules — see residualDeployment.ts). */
    superContribution?: number | string;
    partnerSuperContribution?: number | string;
    superCommenceIncomeStream?: boolean;
    /** An investment (insurance) bond. */
    investmentBond?: number | string;
    [key: string]: unknown;
}

interface ProceedsDeploymentFieldsProps {
    factFind: DraftFactFind;
    /** Lets the client set the investment approach these allocations are priced at. Absent → the
     *  approach is stated but cannot be changed here. */
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    available?: number;
    value?: ProceedsDeploymentValue;
    onChange: (nextDeployment: ProceedsDeploymentValue) => void;
    idPrefix?: string;
}

/**
 * Shared "put the freed-up money to work" allocator.
 *
 * Lets a client deploy a residual lump sum (downsizing proceeds, or the residual after selling a home to
 * fund a granny-flat payment) across shares / managed funds, an investment property, and an annuity — with
 * anything left over staying in the bank. Mirrors the calculation done by
 * {@link applyResidualDeployment} so manual control and the suggestion engine agree.
 *
 * Cleaner, card-based version of the RV sell-home allocator's inline block — built here first for the new
 * modules; a candidate to back-port to RV / Aged Care / Support at Home.
 *
 * @param {Object}   props
 * @param {Object}   props.factFind
 * @param {number}   props.available  - residual available to deploy (the rest goes to the bank)
 * @param {Object}   props.value      - the deployment config (see residualDeployment.js ResidualDeployment)
 * @param {Function} props.onChange   - (nextDeployment) => void
 * @param {string}   [props.idPrefix='deploy'] - DOM id/name prefix to keep radios unique on a page
 */
const ProceedsDeploymentFields = ({ factFind, setFactFind, available = 0, value = {}, onChange, idPrefix = 'deploy' }: ProceedsDeploymentFieldsProps) => {
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;

    const shares = num(value.shares);
    const ip = num(value.investmentProperty);
    const annuity = num(value.annuity);
    const superClient = num(value.superContribution);
    const superPartner = isCouple ? num(value.partnerSuperContribution) : 0;
    const bond = num(value.investmentBond);
    const totalAllocated = shares + ip + annuity + superClient + superPartner + bond;
    const remaining = available - totalAllocated;

    /*
     * AFTER-TAX SUPER (F.6) AND INVESTMENT BONDS (F.5) — 17 Sep 2026.
     *
     * The super card exists only for a person who may contribute (under 75, with room under the
     * total super balance bands): the same all-or-nothing rule the Suggestion Engine applies, so a
     * client is never offered a move the rules refuse. Each field is capped at that person's room,
     * and the engine caps it again when the deployment is applied.
     */
    const superRoom = useMemo(() => nonConcessionalCapacityForHousehold(factFind as unknown as FactFind), [factFind]);
    const clientName = factFind?.client?.firstName || 'you';
    const partnerName = factFind?.partner?.firstName || 'your partner';
    const canCommenceClient = canCommenceAbpFromDownsizer(factFind as unknown as FactFind, 'client');
    const canCommencePartner = isCouple && canCommenceAbpFromDownsizer(factFind as unknown as FactFind, 'partner');
    const bondRatePct = investmentBondNetPctFor(newMoneyProfileOf(factFind));

    const set = (patch: Partial<ProceedsDeploymentValue>) => onChange({ ...value, ...patch });
    // AllocationInput calls onChange(name, val); map name → field.
    const setAmount = (field: string, v: number | undefined) => set({ [field]: v ?? 0, ...deploymentStartingShape(value, field, v) });

    const annuityType: 'lifetime' | 'fixedTerm' = value.annuityType === 'fixedTerm' ? 'fixedTerm' : 'lifetime';
    const annuityOwner: 'client' | 'partner' | 'joint' = deploymentAnnuityOwner(value, isCouple);

    // The freed-up proceeds are the single funding source (the annuity is funded from the residual).
    const deploymentSource: AnnuityFundingSource = { id: 'freed-up-proceeds', type: 'proceeds', label: `Your freed-up proceeds — ${formatCurrency(available)} available`, ownership: annuityOwner, available };
    const fundingSources: AnnuityFundingSource[] = [deploymentSource];

    // Adapter: present the deployment's flat annuity fields to the shared configurator as an annuityDetails object,
    // and map its updates back into the deployment config — so residualDeployment.js still drives the real result.
    const annuityDetails: AnnuityDetails = {
        purchaseAmount: annuity,
        annuityType,
        residualCapital: Math.min(1, Math.max(0, num(value.annuityResidualPercent) / 100)),
        termYears: annuityType === 'fixedTerm' ? (num(value.annuityTermYears) || null) : null,
        deferralYears: annuityType === 'lifetime' ? Math.round(num(value.annuityDeferralYears)) : 0,
        indexation: !!value.annuityIndexation,
        reversionary: !!value.annuityReversionary,
        owner: annuityOwner,
        incomeOverride: value.annuityIncomeOverride != null && value.annuityIncomeOverride !== '' ? parseFloat(String(value.annuityIncomeOverride)) : null,
        fundingSource: deploymentSource,
    };

    const setAnnuityDetails = (updater: AnnuityDetails | ((prev: AnnuityDetails) => AnnuityDetails)) => {
        const next: AnnuityDetails = typeof updater === 'function' ? updater(annuityDetails) : updater;
        set({
            annuity: next.purchaseAmount || 0,
            annuityType: next.annuityType === 'fixedTerm' ? 'fixedTerm' : next.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
            annuityResidualPercent: Math.round((Number(next.residualCapital) || 0) * 100),
            annuityTermYears: Number(next.termYears) || 0,
            annuityDeferralYears: Math.max(0, Math.round(Number(next.deferralYears) || 0)),
            annuityIndexation: !!next.indexation,
            annuityReversionary: !!next.reversionary,
            annuityOwner: next.owner || annuityOwner,
            annuityIncomeOverride: next.incomeOverride ?? undefined,
        });
    };

    // Live annuity outcome — built the SAME way residualDeployment.js builds it, so the preview matches the projection.
    const annuitySummary = useMemo(() => {
        if (!(annuity > 0)) return null;
        try {
            const wantsContinuation = !!value.annuityReversionary || annuityOwner === 'joint';
            const effPartnerAge = (isCouple && wantsContinuation && partnerAge != null) ? partnerAge : null;
            const rec = buildAnnuityImpact(factFind as unknown as FactFind, {
                annuityType,
                purchaseAmount: annuity,
                age: clientAge ?? 65,
                partnerAge: effPartnerAge,
                owner: annuityOwner,
                residualCapital: Math.min(1, Math.max(0, num(value.annuityResidualPercent) / 100)),
                termYears: annuityType === 'fixedTerm' ? (num(value.annuityTermYears) || null) : null,
                deferralYears: annuityType === 'lifetime' ? Math.round(num(value.annuityDeferralYears)) : 0,
                indexation: !!value.annuityIndexation,
                reversionary: !!value.annuityReversionary && !!effPartnerAge,
                incomeOverride: value.annuityIncomeOverride != null && value.annuityIncomeOverride !== '' ? parseFloat(String(value.annuityIncomeOverride)) : null,
            });
            return rec && !rec.warnings ? summariseAnnuityImpact(rec) : null;
        } catch { return null; }
    }, [factFind, annuity, annuityType, annuityOwner, value.annuityReversionary, value.annuityResidualPercent, value.annuityTermYears, value.annuityDeferralYears, value.annuityIndexation, value.annuityIncomeOverride, clientAge, partnerAge, isCouple]);

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900">Put the freed-up money to work</h4>
                    <p className="text-xs text-gray-600">Allocate up to <strong>{formatCurrency(available)}</strong>. Anything you don&apos;t allocate stays in the bank.</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Stays in bank</p>
                    <p className={`text-lg font-bold ${remaining < 0 ? 'text-red-600' : 'text-green-700'}`}>{formatCurrency(remaining)}</p>
                </div>
            </div>
            {remaining < 0 && (
                <p className="text-xs text-red-700 bg-white border border-slate-200 border-l-4 border-l-red-500 rounded-r-lg px-3 py-2">You&apos;ve allocated more than the freed-up money available. Reduce an amount above.</p>
            )}
            {/* Manual Control never invests money nobody committed — but it must not let a large sum
                idle at 2% UNNOTICED either. Loud cash, one visible click to deploy it: pressing the
                button writes the shares field (an explicit, recorded act — the deployment IS the
                provenance), and the engine prices those shares at the client's new-money profile. */}
            {remaining >= 25000 && (
                <div className="rounded-lg bg-white border border-slate-200/70 border-l-4 border-l-amber-500 px-3 py-2.5 shadow-sm flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-slate-700">
                        <strong>{formatCurrency(remaining)}</strong> will sit in the bank earning 2% interest with no growth. That&apos;s a decision too — leave it there, or invest it.
                    </p>
                    <button
                        type="button"
                        onClick={() => set({ shares: shares + remaining })}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
                    >
                        Invest the remainder — {INVESTMENT_PROFILES[newMoneyProfileOf(factFind)].label}
                    </button>
                </div>
            )}

            {/* Shares / managed funds */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <AllocationInput icon={TrendingUp} name="shares" label="Shares / managed funds" value={shares} onChange={setAmount} max={Math.max(available, shares)} tooltip="Invest in a diversified share or managed-fund portfolio. Counted for the pension (deemed income); aims for higher long-term growth than cash, but returns aren't guaranteed." />
                {/* The approach these shares are PRICED at, shown once there is money in them to
                    price. Manual control read this setting and never offered it — see
                    NewMoneyProfileField's header. */}
                {shares > 0 && (
                    <NewMoneyProfileField
                        factFind={factFind}
                        setFactFind={setFactFind}
                        idPrefix={idPrefix}
                        what="this money"
                    />
                )}
            </div>

            {/* Investment property */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <AllocationInput icon={Building} name="investmentProperty" label="Investment property" value={ip} onChange={setAmount} max={Math.max(available, ip)} tooltip="Buy an investment property with the proceeds. The property value is an assessable asset; net rent (after expenses) is counted in the income test." />
                {ip > 0 && (
                    <div className="mt-3 pl-6 border-l-2 border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <CurrencyInput label="Assumed annual rent ($)" value={value.ipRent ?? ''} onChange={(v) => set({ ipRent: v ?? 0 })} className="" />
                        <CurrencyInput label="Assumed annual expenses ($)" value={value.ipExpenses ?? ''} onChange={(v) => set({ ipExpenses: v ?? 0 })} className="" />
                        <p className="text-[11px] text-gray-500 sm:col-span-2">Expenses (rates, insurance, maintenance, management) are generally tax-deductible and reduce the assessable rental income.</p>
                    </div>
                )}
            </div>

            {/* After-tax super — only where the rules let this person contribute */}
            {superRoom.total > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                    {superRoom.client.cap > 0 && (
                        <AllocationInput
                            icon={PiggyBank}
                            name="superContribution"
                            label={isCouple ? `Super — ${clientName} (after-tax contribution)` : 'Super (after-tax contribution)'}
                            value={superClient}
                            onChange={(_n, v) => set({ superContribution: Math.min(superRoom.client.cap, v ?? 0) })}
                            max={Math.min(Math.max(available, superClient), superRoom.client.cap)}
                            tooltip={`A non-concessional contribution: money that has already been taxed goes into super, where earnings are taxed at 15% (nil once it is paying a pension) instead of at your own rate. ${clientName === 'you' ? 'You' : clientName} may contribute up to ${formatCurrency(superRoom.client.cap)} now${superRoom.client.years > 1 ? ` (${superRoom.client.years} years of the cap brought forward)` : ''}.${superRoom.client.exemptFromMeansTests ? ' Being under Age Pension age, super kept in accumulation is not counted by Centrelink until pension age.' : ''}`}
                        />
                    )}
                    {isCouple && superRoom.partner.cap > 0 && (
                        <AllocationInput
                            icon={PiggyBank}
                            name="partnerSuperContribution"
                            label={`Super — ${partnerName} (after-tax contribution)`}
                            value={superPartner}
                            onChange={(_n, v) => set({ partnerSuperContribution: Math.min(superRoom.partner.cap, v ?? 0) })}
                            max={Math.min(Math.max(available, superPartner), superRoom.partner.cap)}
                            tooltip={`${partnerName} may contribute up to ${formatCurrency(superRoom.partner.cap)} now${superRoom.partner.years > 1 ? ` (${superRoom.partner.years} years of the cap brought forward)` : ''}.${superRoom.partner.exemptFromMeansTests ? ' Being under Age Pension age, super kept in accumulation is not counted by Centrelink until pension age.' : ''}`}
                        />
                    )}
                    {/* WHY ONE OF A COUPLE HAS NO FIELD (owner, 2 Oct 2026: "Cameron can't contribute
                        due to his age" belongs on the screen). The rule is logic-based — a person who
                        cannot contribute gets no input — and the reason is said in one line so the
                        missing field reads as the law, not an omission. */}
                    {isCouple && superRoom.client.cap <= 0 && superRoom.client.blocker && (
                        <p className="text-xs text-slate-600">{nonConcessionalBlockerWords(clientName, superRoom.client.blocker)}</p>
                    )}
                    {isCouple && superRoom.partner.cap <= 0 && superRoom.partner.blocker && superRoom.partner.blocker !== 'no_person' && (
                        <p className="text-xs text-slate-600">{nonConcessionalBlockerWords(partnerName, superRoom.partner.blocker)}</p>
                    )}
                    {(superClient > 0 || superPartner > 0) && (
                        <div className="pl-6 border-l-2 border-slate-200 space-y-1.5">
                            {(canCommenceClient || canCommencePartner) && (
                                <label className="flex items-start gap-2 text-xs text-slate-700">
                                    <input type="checkbox" className="mt-0.5" checked={!!value.superCommenceIncomeStream} onChange={(e) => set({ superCommenceIncomeStream: e.target.checked })} />
                                    <span>Start an account-based pension with it (earnings tax-free; the minimum pension is drawn each year){superRoom.preferred ? ` — leaves ${superRoom.preferred === 'client' ? clientName : partnerName}'s money counted by Centrelink, where accumulation super is not while they are under Age Pension age` : ''}.</span>
                                </label>
                            )}
                            {(superRoom.client.balanceEstimated || superRoom.partner.balanceEstimated) && (
                                <p className="text-[11px] text-slate-500">The cap is worked out from the super balances recorded here. The real figure is the total super balance at the last 30 June on the ATO record; check it before relying on a contribution near the limit.</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Investment bond */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <AllocationInput
                    icon={Gem}
                    name="investmentBond"
                    label="Investment bond"
                    value={bond}
                    onChange={setAmount}
                    max={Math.max(available, bond)}
                    tooltip={`An investment (insurance) bond pays its own tax inside at 30% and pays nothing out to you; after ${INVESTMENT_BOND_TAX_FREE_YEARS} years withdrawals are tax-free. Priced at your ${INVESTMENT_PROFILES[newMoneyProfileOf(factFind)].label} approach, that is about ${bondRatePct}% a year net. Centrelink counts it in full and deems it, like cash or shares. The projection leaves it untouched for ${INVESTMENT_BOND_TAX_FREE_YEARS} years, so it suits money you will not need before then${superRoom.total > 0 ? ' — super is usually the better home first, while the rules still let you contribute' : ' — the usual home for money super can no longer take'}.`}
                />
            </div>

            {/* Annuity */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <AllocationInput icon={ShieldCheck} name="annuity" label="Annuity (guaranteed income)" value={annuity} onChange={setAmount} max={Math.max(available, annuity)} tooltip="Convert a lump sum into guaranteed income. For a lifetime annuity only 60% of the purchase price is counted for the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which can lift the Age Pension." />
                {/* Above the client's ceiling: a caution, not a block (guaranteed income review M3, 21 Oct 2026). */}
                <AnnuityCeilingCaution factFind={factFind} amount={annuity} investable={available} testId={`${idPrefix}-annuity-ceiling-caution`} />
                {annuity > 0 && (
                    <div className="mt-3">
                        <SupportAtHomeAnnuityConfigurator
                            factFind={factFind}
                            annuityDetails={annuityDetails}
                            setAnnuityDetails={setAnnuityDetails}
                            annuitySummary={annuitySummary}
                            annuityWarnings={[]}
                            fundingSources={fundingSources}
                            formatCurrency={formatCurrency}
                            clientAge={clientAge}
                            partnerAge={partnerAge}
                            hideAmount
                            onIncomeOnlyUnderstoodChange={incomeOnlySetter(setFactFind)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProceedsDeploymentFields;
